import React, { useEffect, useRef, useState } from 'react';
import { Modal, Descriptions, Spin, Alert, Button, Empty, Space, Typography, Table } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import type { ColumnsType } from 'antd/es/table';
import {
  getUserDetails,
  type UserDetailsResponse,
  type LegalConsentDetail,
  type AuthProviderDetail,
  type ValidationAuditEventDetail,
  type SurveyResponseDetail,
} from '../../services/admin-users.service';
import { genderLabels, educationLabels, economicLabels } from '../../utils/profile-labels';
import { getUserFriendlyMessage } from '../../utils/error-handler';

const { Text } = Typography;

export interface UserDetailsModalProps {
  userId: number | null;
  open: boolean;
  onClose: () => void;
}

const ROLE_LABELS: Record<string, string> = {
  participant: 'Participante',
  contractor: 'Contratante',
  administrator: 'Administrador',
};

const AUTH_PROVIDER_LABELS: Record<string, string> = {
  PASSWORD: 'Senha',
  GOOGLE: 'Google',
};

const LEGAL_DOCUMENT_TYPE_LABELS: Record<string, string> = {
  TERMS_OF_SERVICE: 'Termos de Uso',
  PRIVACY_POLICY: 'Política de Privacidade',
  COOKIE_POLICY: 'Política de Cookies',
};

const LEGAL_CONSENT_ACTOR_LABELS: Record<string, string> = {
  USER_SELF: 'Próprio usuário',
  ADMIN_ASSISTED: 'Assistido por admin',
};

function formatDateTime(value: string | null): string {
  return value ? dayjs(value).format('DD/MM/YYYY HH:mm') : '-';
}

function withRowKeys<T>(items: T[]): (T & { key: number })[] {
  return items.map((item, index) => ({ ...item, key: index }));
}

const legalConsentColumns: ColumnsType<LegalConsentDetail & { key: number }> = [
  {
    title: 'Documento',
    dataIndex: 'documentType',
    render: (value: string) => LEGAL_DOCUMENT_TYPE_LABELS[value] || value,
  },
  { title: 'Versão', dataIndex: 'version' },
  { title: 'Aceito em', dataIndex: 'acceptedAt', render: formatDateTime },
  { title: 'IP', dataIndex: 'ipAddress', render: (value: string | null) => value || '-' },
  {
    title: 'Ator',
    dataIndex: 'actor',
    render: (value: string) => LEGAL_CONSENT_ACTOR_LABELS[value] || value,
  },
];

const authProviderColumns: ColumnsType<AuthProviderDetail & { key: number }> = [
  {
    title: 'Provedor',
    dataIndex: 'provider',
    render: (value: string) => AUTH_PROVIDER_LABELS[value] || value,
  },
  { title: 'Vinculado em', dataIndex: 'linkedAt', render: formatDateTime },
  { title: 'Último uso', dataIndex: 'lastUsedAt', render: formatDateTime },
];

const validationAuditColumns: ColumnsType<ValidationAuditEventDetail & { key: number }> = [
  { title: 'Evento', dataIndex: 'eventType' },
  { title: 'Ator', dataIndex: 'actorType' },
  { title: 'Data', dataIndex: 'createdAt', render: formatDateTime },
  {
    title: 'Correlation ID',
    dataIndex: 'correlationId',
    render: (value: string) => (
      <Text code style={{ fontSize: 11 }}>
        {value}
      </Text>
    ),
  },
];

const surveyResponseColumns: ColumnsType<SurveyResponseDetail & { key: number }> = [
  { title: 'Pesquisa (ID)', dataIndex: 'surveyId' },
  { title: 'Enviado em', dataIndex: 'submittedAt', render: formatDateTime },
  { title: 'IP', dataIndex: 'ipAddress', render: (value: string | null) => value || '-' },
  {
    title: 'User Agent',
    dataIndex: 'userAgent',
    render: (value: string | null) => (
      <Text style={{ fontSize: 11 }} ellipsis={{ tooltip: value || undefined }}>
        {value || '-'}
      </Text>
    ),
  },
  {
    title: 'Device Fingerprint',
    dataIndex: 'deviceFingerprint',
    render: (value: string | null) =>
      value ? (
        <Text code style={{ fontSize: 11 }}>
          {value}
        </Text>
      ) : (
        '-'
      ),
  },
];

export const UserDetailsModal: React.FC<UserDetailsModalProps> = ({ userId, open, onClose }) => {
  // Garante uma nova busca a cada abertura da modal, mesmo para o mesmo usuário (FR-002)
  const [fetchNonce, setFetchNonce] = useState(0);
  const wasOpen = useRef(false);

  useEffect(() => {
    if (open && !wasOpen.current) {
      setFetchNonce((n) => n + 1);
    }
    wasOpen.current = open;
  }, [open]);

  const {
    data,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery<UserDetailsResponse>({
    queryKey: ['admin', 'user-details', userId, fetchNonce],
    queryFn: () => getUserDetails(userId as number),
    enabled: open && !!userId && fetchNonce > 0,
    staleTime: 0,
    gcTime: 0,
    retry: false,
  });

  return (
    <Modal
      title="Detalhes do Usuário"
      open={open}
      onCancel={onClose}
      footer={<Button onClick={onClose}>Fechar</Button>}
      width={840}
      destroyOnClose
    >
      {isLoading && (
        <div style={{ textAlign: 'center', padding: '40px 0' }}>
          <Spin size="large" />
        </div>
      )}

      {isError && (
        <Alert
          type="error"
          showIcon
          message="Erro ao carregar detalhes do usuário"
          description={getUserFriendlyMessage(error)}
          action={
            <Button size="small" icon={<ReloadOutlined />} onClick={() => refetch()}>
              Tentar novamente
            </Button>
          }
        />
      )}

      {!isLoading && !isError && data && (
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <Descriptions title="Usuário" column={2} bordered size="small">
            <Descriptions.Item label="Nome" span={2}>
              {data.user.preferredName || '-'}
            </Descriptions.Item>
            <Descriptions.Item label="E-mail" span={2}>
              {data.user.email}
            </Descriptions.Item>
            <Descriptions.Item label="Telefone">{data.user.phone}</Descriptions.Item>
            <Descriptions.Item label="Perfil">
              {ROLE_LABELS[data.user.role] || data.user.role}
            </Descriptions.Item>
            <Descriptions.Item label="Conta ativa">
              {data.user.active ? 'Sim' : 'Não'}
            </Descriptions.Item>
            <Descriptions.Item label="Criado em">
              {formatDateTime(data.user.createdAt)}
            </Descriptions.Item>
          </Descriptions>

          <div>
            <Text strong>Perfil de Participante</Text>
            {data.participantProfile ? (
              <Descriptions column={2} bordered size="small" style={{ marginTop: 8 }}>
                <Descriptions.Item label="Gênero">
                  {genderLabels[data.participantProfile.gender] || data.participantProfile.gender}
                </Descriptions.Item>
                <Descriptions.Item label="Data de Nascimento">
                  {dayjs(data.participantProfile.dateOfBirth).format('DD/MM/YYYY')}
                </Descriptions.Item>
                <Descriptions.Item label="Escolaridade">
                  {educationLabels[data.participantProfile.educationLevel] ||
                    data.participantProfile.educationLevel}
                </Descriptions.Item>
                <Descriptions.Item label="Nível Econômico">
                  {economicLabels[data.participantProfile.economicLevel] ||
                    data.participantProfile.economicLevel}
                </Descriptions.Item>
                <Descriptions.Item label="Cidade">
                  {data.participantProfile.cityName || '-'}
                </Descriptions.Item>
                <Descriptions.Item label="Bairro">
                  {data.participantProfile.neighborhood}
                </Descriptions.Item>
                <Descriptions.Item label="Consentimento de E-mail Marketing">
                  {data.participantProfile.emailMarketingConsent ? 'Sim' : 'Não'}
                </Descriptions.Item>
                <Descriptions.Item label="Consentimento de Cookies">
                  {data.participantProfile.cookieMarketingConsent === null
                    ? '-'
                    : data.participantProfile.cookieMarketingConsent
                      ? 'Sim'
                      : 'Não'}
                </Descriptions.Item>
                <Descriptions.Item label="Última atualização" span={2}>
                  {formatDateTime(data.participantProfile.lastUpdatedAt)}
                </Descriptions.Item>
              </Descriptions>
            ) : (
              <Empty
                description="Sem perfil de participante"
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                style={{ marginTop: 8 }}
              />
            )}
          </div>

          <div>
            <Text strong>Consentimentos Legais</Text>
            <Table
              style={{ marginTop: 8 }}
              size="small"
              pagination={false}
              dataSource={withRowKeys(data.legalConsents)}
              locale={{ emptyText: 'Sem registros' }}
              columns={legalConsentColumns}
            />
          </div>

          <div>
            <Text strong>Provedores de Autenticação</Text>
            <Table
              style={{ marginTop: 8 }}
              size="small"
              pagination={false}
              dataSource={withRowKeys(data.authProviders)}
              locale={{ emptyText: 'Sem registros' }}
              columns={authProviderColumns}
            />
          </div>

          <div>
            <Text strong>Eventos de Auditoria de Validação</Text>
            <Table
              style={{ marginTop: 8 }}
              size="small"
              pagination={false}
              dataSource={withRowKeys(data.validationAuditEvents.items)}
              locale={{ emptyText: 'Sem registros' }}
              columns={validationAuditColumns}
            />
            {data.validationAuditEvents.hasMore && (
              <Text type="secondary" style={{ fontSize: 12 }}>
                Exibindo os {data.validationAuditEvents.items.length} eventos mais recentes. Há
                mais eventos além dos exibidos.
              </Text>
            )}
          </div>

          <div>
            <Text strong>Respostas de Pesquisa</Text>
            <Table
              style={{ marginTop: 8 }}
              size="small"
              pagination={false}
              dataSource={withRowKeys(data.surveyResponses.items)}
              locale={{ emptyText: 'Sem registros' }}
              columns={surveyResponseColumns}
              scroll={{ x: 700 }}
            />
            {data.surveyResponses.hasMore && (
              <Text type="secondary" style={{ fontSize: 12 }}>
                Exibindo as {data.surveyResponses.items.length} respostas mais recentes. Há mais
                respostas além das exibidas.
              </Text>
            )}
          </div>
        </Space>
      )}
    </Modal>
  );
};

export default UserDetailsModal;
